/**
 * StaffWithdrawPage — J-1-2. 직원 계정 탈퇴
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Alert, Button, Card, Checkbox, Input, Page, PageHeader, Skeleton } from '@indipia/ui-core';
import { useAuthStore } from '@indipia/shared';
import {
  withdrawUser,
  verifyPassword,
  requestPhoneOtp,
  verifyPhoneOtp,
  fetchProfile,
} from '../api/profile';
import { fetchJoinStatus } from '../api/employees';

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

export function StaffWithdrawPage() {
  const navigate  = useNavigate();
  const logout    = useAuthStore((s) => s.logout);
  const user      = useAuthStore((s) => s.user);
  const { data: joinStatus, isLoading: joinLoading } = useQuery({
    queryKey: ['join-status'],
    queryFn: fetchJoinStatus,
  });

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: fetchProfile,
  });

  // 로딩 중에는 SNS 여부를 확정하지 않는다 — 미확정 분기는 폼이 교체되는 깜빡임을 만든다 (D-07 §17.1)
  const isSns = profile?.snsYn === 'Y';

  // 본인 확인
  const [verified,    setVerified]    = useState(false);
  const [agreed,      setAgreed]      = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // SNS — OTP
  const [mobile,      setMobile]      = useState('');
  const [mobileError, setMobileError] = useState<string | null>(null);
  const [otpSent,     setOtpSent]     = useState(false);
  const [otpCode,     setOtpCode]     = useState('');
  const [otpSending,  setOtpSending]  = useState(false);

  // 일반 — 비밀번호
  const [password, setPassword] = useState('');

  const withdrawMutation = useMutation({
    mutationFn: () => withdrawUser(),
    onSuccess: () => {
      logout();
      navigate('/login', { replace: true });
    },
    onError: () => setSubmitError('탈퇴 처리에 실패했습니다. 다시 시도해주세요.'),
  });

  async function handlePasswordVerify() {
    if (!password) { setVerifyError('비밀번호를 입력해주세요.'); return; }
    setVerifyError(null);
    try {
      const ok = await verifyPassword(password);
      if (ok) setVerified(true);
      else setVerifyError('비밀번호가 일치하지 않습니다.');
    } catch {
      setVerifyError('본인 확인에 실패했습니다. 다시 시도해주세요.');
    }
  }

  function handleMobileBlur() {
    if (!mobile) return;
    const registered = profile?.mobile;
    if (!registered) return;
    if (mobile !== registered) {
      setMobileError('가입하신 휴대폰번호와 일치하지 않습니다.');
    } else {
      setMobileError(null);
    }
  }

  async function handleOtpSend() {
    if (!mobile) { setVerifyError('휴대폰번호를 입력해주세요.'); return; }
    const registered = profile?.mobile;
    if (registered && mobile !== registered) {
      setMobileError('가입하신 휴대폰번호와 일치하지 않습니다.');
      return;
    }
    setVerifyError(null);
    setMobileError(null);
    setOtpSending(true);
    try {
      await requestPhoneOtp(mobile);
      setOtpSent(true);
    } catch {
      setVerifyError('인증번호 발송에 실패했습니다.');
    } finally {
      setOtpSending(false);
    }
  }

  async function handleOtpVerify() {
    if (!otpCode) { setVerifyError('인증번호를 입력해주세요.'); return; }
    setVerifyError(null);
    try {
      const res = await verifyPhoneOtp({ mobile, verifyCode: otpCode });
      if (res.verified) setVerified(true);
      else setVerifyError('인증번호가 일치하지 않습니다.');
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      if (code === 'CODE_MISMATCH') setVerifyError('인증번호가 일치하지 않습니다.');
      else if (code === 'CODE_EXPIRED') setVerifyError('인증번호가 만료됐어요. 다시 발송해주세요.');
      else setVerifyError('인증에 실패했습니다.');
    }
  }

  function handleWithdraw() {
    if (!verified) { setSubmitError('본인 확인이 필요합니다.'); return; }
    if (!agreed)   { setSubmitError('탈퇴 동의를 체크해주세요.'); return; }
    setSubmitError(null);
    withdrawMutation.mutate();
  }

  return (
    <Page width="sm">
      <PageHeader title="직원 계정 탈퇴" subtitle="탈퇴 전 안내 사항을 꼭 확인해 주세요" />

      {/* 경고 */}
      <Alert kind="warning" title="탈퇴 전 꼭 확인하세요" className="mb-4">
        <ul className="space-y-1">
          <li>탈퇴 시 현재 소속이 자동 해제되며, 계정 복구가 불가합니다.</li>
          <li>소속 해제 후 마스터에게 통보됩니다.</li>
          <li>탈퇴 후 30일간 데이터 보관 후 영구 삭제됩니다.</li>
        </ul>
      </Alert>

        {/* 현재 소속 카드 — 로딩 중 '소속 업체 없음'이 확정 사실처럼 보이면 안 된다 (D-07 §17.1) */}
        <Card className="mb-5">
          <p className="mb-1.5 text-caption font-bold uppercase tracking-widest text-text-muted">현재 소속</p>
          {joinLoading ? (
            <Skeleton height={20} width="60%" />
          ) : (
            <p className="text-sm font-semibold text-text">
              {joinStatus?.companyName ?? '소속 업체 없음'}
            </p>
          )}
        </Card>

        {/* 본인 확인 영역 */}
        <Card className="mb-4">
          <div className="space-y-3">
          <p className="text-sm font-bold text-text">본인 확인</p>

          {profileLoading ? (
            <Skeleton height={40} />
          ) : isSns ? (
            <>
              <div className="flex items-start gap-2">
                <Input
                  value={mobile}
                  onChange={(e) => { setMobile(formatPhone(e.target.value)); setMobileError(null); }}
                  onBlur={handleMobileBlur}
                  placeholder="010-0000-0000"
                  width="fill"
                  error={mobileError ?? undefined}
                  disabled={otpSent || verified}
                />
                <Button
                  variant="secondary"
                  onClick={handleOtpSend}
                  disabled={otpSent || otpSending || verified}
                  loading={otpSending}
                  className="shrink-0"
                >
                  {otpSent ? '발송됨' : '발송'}
                </Button>
              </div>
              {otpSent && !verified && (
                <div className="flex items-start gap-2">
                  <Input
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    placeholder="인증번호 6자리"
                    maxLength={6}
                    width="fill"
                    error={verifyError ?? undefined}
                  />
                  <Button variant="primary" onClick={handleOtpVerify} className="shrink-0">인증</Button>
                </div>
              )}
            </>
          ) : (
            !verified && (
              <div className="flex items-start gap-2">
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="비밀번호 입력"
                  width="fill"
                  error={verifyError ?? undefined}
                />
                <Button variant="primary" onClick={handlePasswordVerify} className="shrink-0">인증</Button>
              </div>
            )
          )}

          {verified && <Alert kind="success">본인 확인이 완료됐어요</Alert>}

          {/* OTP 발송 단계의 오류는 아직 입력칸에 붙일 수 없어 여기 남는다 */}
          {isSns && !otpSent && verifyError && <Alert kind="error">{verifyError}</Alert>}
          </div>
        </Card>

        {/* 동의 체크박스 */}
        <Checkbox
          className="mb-4"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          label="위 내용을 확인하였으면 탈퇴에 동의합니다."
        />

        {submitError && <Alert kind="error" className="mb-3">{submitError}</Alert>}

        {/* 버튼 영역 */}
        <div className="flex gap-3">
          <Button variant="primary" size="lg" onClick={() => navigate('/settings/profile')} className="flex-1">
            취소
          </Button>
          <Button
            variant="etc"
            size="lg"
            onClick={handleWithdraw}
            disabled={!verified || !agreed || withdrawMutation.isPending}
            loading={withdrawMutation.isPending}
            className="flex-1"
          >
            탈퇴 확인
          </Button>
        </div>

        {user?.memberName && (
          <p className="mt-5 text-center text-caption text-text-muted">
            탈퇴 대상 계정: <strong>{user.memberName}</strong>
          </p>
        )}
    </Page>
  );
}
